import api from "app/lib/api";

import { getFormattedDate } from "utils/date";
import { interpolate } from "utils/string";

import endpoints from "static/endpoints";
import { DISCONNECTED_MONITOR_DOWNLOAD_TYPES } from "static/constants";

export const getPatients = async (query, isExport = false) => {
  let url = isExport
    ? `${endpoints.patient.downloadList}?`
    : `${endpoints.patient.list}?`;

  const {
    filters,
    search,
    page,
    pageSize,
    sorting,
    showPatientAlert,
    showDeviceAlert,
    showMissingContactInfo,
    showAlertWhenDisconnected,
  } = query || {};

  if (filters?.length > 0) {
    const filterQueries = filters
      ?.map((filter) => {
        const { id, value } = filter;

        if (id === "nextSend") {
          if (value?.startDate && value?.endDate) {
            const start = getFormattedDate(value.startDate, "MM/DD/YYYY");
            const end = getFormattedDate(value.endDate, "MM/DD/YYYY");

            return start === end
              ? `nextSend=${start}&`
              : `nextSend=${start}-${end}&`;
          }

          const nextSendDate = getFormattedDate(value, "MM/DD/YYYY");
          return `nextSend=${nextSendDate}&`;
        }

        // A picked list is matched exactly, typed text as a pattern — so they travel
        // as separate parameters.
        if (id === "hospital") {
          return Array.isArray(value)
            ? `hospitalNames=${value.join(",")}&`
            : `hospital=${value}&`;
        }

        if (["dob", "doi", "lastBillDate"].includes(id)) {
          const newDate = getFormattedDate(value, "YYYY-MM-DD");
          return `${id}=${newDate}&`;
        }

        return `${id}=${value}&`;
      })
      ?.filter(Boolean)
      ?.join("");

    url += filterQueries;
  }

  if (sorting?.length > 0) {
    const sortingQueries = sorting
      ?.map((sort) => {
        const { id, desc } = sort;
        const direction = desc ? "desc" : "asc";

        return `sort=${id}&order=${direction}&`;
      })
      ?.filter(Boolean)
      ?.join("");

    url += sortingQueries;
  }

  if (search) url += `search=${search}&`;
  if (showPatientAlert) url += `showPatientAlert=${showPatientAlert}&`;
  if (showDeviceAlert) url += `showDeviceAlert=${showDeviceAlert}&`;
  if (showMissingContactInfo)
    url += `missingContactInfo=${showMissingContactInfo}&`;
  if (showAlertWhenDisconnected)
    url += `alertWhenDisconnected=${showAlertWhenDisconnected}&`;

  url += `page=${page}&pageSize=${pageSize}`;

  if (isExport) {
    return await api.get(url, { responseType: "blob" });
  } else {
    const { data, meta } = await api.get(url);

    return { data, meta };
  }
};

export const getDisconnectedMonitors = async (payload, downloadType) => {
  const url =
    downloadType === DISCONNECTED_MONITOR_DOWNLOAD_TYPES.disconnectedMonitorList
      ? endpoints.patient.downloadDisconnectedMonitors
      : endpoints.patient.downloadNoShowMasterList;
  const { data, headers } = await api.post(url, payload, {
    responseType: "blob",
  });
  const fileHeader = headers?.get("Content-Disposition");
  const filename = fileHeader?.split("filename=")[1];
  return { data, filename };
};

export const enrollPatient = async (payload) => {
  const url = endpoints.patient.enroll;
  const response = await api.post(url, payload);

  return response;
};

export const updatePatientById = async (patientId, payload) => {
  const url = interpolate(endpoints.patient.updateActive, { patientId });

  const response = await api.put(url, payload);

  return response;
};

export const fetchDeviceAlertList = async (filter) => {
  const { data } = await api.get(endpoints.patient.fetchDeviceAlertList, {
    params: {
      ...filter,
    },
  });

  return data;
};

export const addPatientNote = async (payload) => {
  const { data } = await api.post(endpoints.patient.addPatientNote, payload);

  return data;
};

export const fetchSubscriptionPauseHistory = async (patientId) => {
  const url = interpolate(endpoints.patient.getSubscriptionPauseHistory, {
    patientId,
  });
  const { data } = await api.get(url);

  return data;
};

export const fetchPatientNotesHistory = async (patientId) => {
  const url = interpolate(endpoints.patient.getPatientNotesHistory, {
    patientId,
  });
  const { data } = await api.get(url);

  return data;
};

export const findPatientRecordsByName = async (
  value,
  searchByDeviceSerialNumber,
  facility,
  account
) => {
  let url = `${endpoints.patient.findPatientByName}?value=${value}&searchByDeviceSerialNumber=${searchByDeviceSerialNumber}`;

  if (facility) {
    url += `&facility=${facility}`;
  }
  if (account && account !== "All") {
    url += `&account=${account}`;
  }
  const { data } = await api.get(url);
  return data;
};

export const findPatientRecordsByNameOrDobOrEvokeId = async (value) => {
  const url = `${endpoints.patient.findPatientRecordsByNameOrDobOrEvokeId}?value=${value}`;
  const { data } = await api.get(url);
  return data;
};

export const formSettings = async () => {
  const url = endpoints.patient.formSettings;
  return await api.get(url);
};

export const patientDataPoints = async (patientId) => {
  const url = `${endpoints.patient.profile}/${patientId}/datapoint`;
  return await api.get(url);
};

export const storePatientDataPoint = async (patientId, formData, isUpdate) => {
  let response;
  const url = `${endpoints.patient.profile}/${patientId}/datapoint`;

  if (!formData) return;

  if (isUpdate) {
    response = await api.put(url, formData);
  } else {
    response = await api.post(url, formData);
  }
  return response;
};

export const downloadBillingHistory = async (payload) => {
  const url = endpoints.patient.downloadBillingHistory;

  const { data, headers } = await api.post(url, payload, {
    responseType: "blob",
  });

  const filename = headers?.get("Content-Disposition")?.split("filename=")?.[1];

  return { data, filename };
};

export const getReminderPatients = async (values) => {
  // Construct the base URL
  let url = `${endpoints.patient.downloadReminderPatients}`;

  // Filter out null or undefined values and map to query parameters
  const queryParams = Object.entries(values)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  // Append the query parameters if they exist
  if (queryParams) {
    url += `?${queryParams}`;
  }

  // Send the API request and return the blob response
  return await api.post(url, {}, { responseType: "blob" });
};

export const getPatientsFromEvokeIds = async (evokeIds) => {
  let url = `${endpoints.patient.patientsByEvokeId}`;
  const queryParams = `evokeIds=${encodeURIComponent(evokeIds.join())}`;

  if (queryParams) {
    url += `?${queryParams}`;
  }

  return await api.get(url);
};

export const mergePatientRecords = async (payload) => {
  const url = `${endpoints.patient.mergePatients}`;
  return await api.post(url, payload);
};

export const updatePatientAdvisory = async (payload) => {
  return await api.patch(endpoints.patient.patientAdvisory, payload);
};

export const updateBenchmarkId = async (patientId, payload) => {
  return await api.put(
    interpolate(endpoints.patient.updateBenchmarkId, { patientId }),
    payload
  );
};

export const searchPatients = async (payload) => {
  const queryString = new URLSearchParams(payload).toString();
  const url = `${endpoints.patient.searchPatients}?${queryString}`;
  return await api.get(url);
};

export const updateSelfPayStatus = async (patientId, selfPay) => {
  return await api.patch(
    interpolate(endpoints.patient.updateSelfPayStatus, { patientId }),
    { selfPay }
  );
};

export const updatePreAuthStatus = async (patientId, preAuth) => {
  return await api.patch(
    interpolate(endpoints.patient.updatePreAuthStatus, { patientId }),
    { preAuth }
  );
};

export const patientNoteRequestToDelete = async (payload) => {
  const { patientId, noteId, reason } = payload;

  const url = interpolate(endpoints.task.requestNoteDeletion, {
    patientId,
    noteId,
  });

  return await api.put(url, {
    reason,
  });
};
